import { Box, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { MaterialQuantity } from "src/store/api/generatedApi";
import {
  isValidOverrideQuantity,
  MAX_OVERRIDE_QUANTITY,
  MIN_OVERRIDE_QUANTITY,
} from "./quantity";

export const OverrideIngredientControl = ({
  materialName,
  quantity,
  disabled,
  onChange,
}: {
  materialName: string;
  quantity: MaterialQuantity;
  disabled: boolean;
  onChange: (quantity: MaterialQuantity) => void;
}) => {
  const { t } = useTenantTranslation();
  const unitLabel = t(quantity.unit === "ratio" ? "ratio" : "grabs");
  return (
    <Box
      sx={{
        minWidth: 0,
        display: "grid",
        gridTemplateRows: "subgrid",
        gridRow: "span 2",
      }}
    >
      <Typography
        variant="body2Mono"
        component="div"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 0.5,
          py: 0.5,
          fontWeight: "fontWeightBold",
          textAlign: "center",
          bgcolor: "grey.50",
          borderBottom: 1,
          borderColor: "text.primary",
        }}
      >
        {materialName}
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 0.5,
          px: 0.5,
          py: 1,
          bgcolor: "background.paper",
        }}
      >
        <Tooltip title={unitLabel}>
          <IconButton
            aria-label={t("overrideRecipeUnit", {
              material: materialName,
              unit: unitLabel,
            })}
            disabled={disabled}
            color="primary"
            onClick={() => {
              onChange({
                ...quantity,
                unit: quantity.unit === "ratio" ? "grab" : "ratio",
              });
            }}
            sx={{
              flexShrink: 0,
              width: 26,
              height: 26,
              p: 0,
              borderRadius: "50%",
              bgcolor: "grey.100",
            }}
          >
            <Box
              component="svg"
              viewBox="0 0 20 20"
              aria-hidden="true"
              sx={{
                width: 20,
                height: 20,
                display: "block",
                fill: "currentColor",
              }}
            >
              {quantity.unit === "ratio" ? (
                <>
                  <circle cx="10" cy="6.5" r="1.8" />
                  <circle cx="10" cy="13.5" r="1.8" />
                </>
              ) : (
                <path d="M8.5 4h3v4.5H16v3h-4.5V16h-3v-4.5H4v-3h4.5z" />
              )}
            </Box>
          </IconButton>
        </Tooltip>
        <TextField
          type="number"
          variant="standard"
          value={Number.isFinite(quantity.value) ? quantity.value : ""}
          disabled={disabled}
          error={!isValidOverrideQuantity(quantity.value)}
          onChange={(event) => {
            onChange({
              ...quantity,
              value:
                event.target.value === ""
                  ? Number.NaN
                  : Number(event.target.value),
            });
          }}
          slotProps={{
            input: { disableUnderline: true },
            htmlInput: {
              min: MIN_OVERRIDE_QUANTITY,
              max: MAX_OVERRIDE_QUANTITY,
              step: "any",
              "aria-label": materialName,
            },
          }}
          sx={{
            minWidth: 0,
            width: `calc(${Math.max(2, String(quantity.value).length)}ch + 18px)`,
            maxWidth: "calc(100% - 30px)",
            typography: "h3",
            borderRadius: 0.5,
            "&:hover, &:focus-within": {
              bgcolor: "action.hover",
            },
            "& input": {
              typography: "h3",
              textAlign: "center",
              px: 0,
            },
          }}
        />
      </Box>
    </Box>
  );
};
